// src/main.tsx
import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import { PortalLanguageProvider } from "@/contexts/PortalLanguageContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";

/* ── Sentry ─────────────────────────────────────────────────────────── */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,           // "production" | "development"
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,  // 10% traces in prod, none in dev
    replaysSessionSampleRate: 0,                 // opt-in only — no session replay by default
    replaysOnErrorSampleRate: 1.0,               // always capture replay on error
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Do not send errors from localhost dev sessions
    beforeSend(event) {
      if (import.meta.env.DEV) return null;
      return event;
    },
  });
}

// Legacy unhandled error logging (retained for non-Sentry environments / local dev)
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    console.error(
      "[window.onerror]",
      e?.message,
      e?.filename,
      e?.lineno,
      e?.colno,
      e?.error
    );
  });
  window.addEventListener("unhandledrejection", (e) => {
    console.error("[unhandledrejection]", e?.reason);
  });
}

function Fallback() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center">
      <div className="animate-pulse text-gray-400">Loading…</div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <React.Suspense fallback={<Fallback />}>
        <PortalLanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </PortalLanguageProvider>
      </React.Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);
